// Multi-tenant Events & Auditorium platform — complete API client.
// Every institute (BMSIT, SVIT, ...) shares this one frontend + one Worker;
// the X-Institute header tells the Worker which institute's own database,
// branding and email config to use for the request.
const API_URL = process.env.REACT_APP_API_URL || "https://bmsit-auditorium-api.info-fortifyhub.workers.dev";
const TOKEN_KEY = "events.token";
const USER_KEY  = "events.user";
const INSTITUTE_KEY = "events.institute"; // slug, e.g. "bmsit" or "svit"
const PLATFORM_TOKEN_KEY = "events.platform.token";

// One-time migration for anyone who was signed in BEFORE this multi-tenant
// update (keys were "bmsit.token" / "bmsit.user" / "bmsit.theme", and there
// was no institute concept at all — everyone was implicitly BMSIT). Without
// this, existing BMSIT users would look logged-out on first load even with
// their AUTH_SECRET preserved server-side, and lose their current theme.
(function migrateLegacyKeys() {
  try {
    if (localStorage.getItem(TOKEN_KEY)) return; // already migrated / new install
    const legacyToken = localStorage.getItem("bmsit.token");
    if (!legacyToken) return;
    localStorage.setItem(TOKEN_KEY, legacyToken);
    const legacyUser = localStorage.getItem("bmsit.user");
    if (legacyUser) localStorage.setItem(USER_KEY, legacyUser);
    const legacyTheme = localStorage.getItem("bmsit.theme");
    if (legacyTheme) localStorage.setItem("events.theme", legacyTheme);
    // Pre-multi-tenant users only ever knew one institute: BMSIT.
    if (!localStorage.getItem(INSTITUTE_KEY)) localStorage.setItem(INSTITUTE_KEY, "bmsit");
  } catch {}
})();

export const getToken      = () => localStorage.getItem(TOKEN_KEY) || "";
export const setToken      = t  => t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY);
export const getStoredUser = () => { try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; } };
export const setStoredUser = u  => u ? localStorage.setItem(USER_KEY, JSON.stringify(u)) : localStorage.removeItem(USER_KEY);

export const getInstitute  = () => localStorage.getItem(INSTITUTE_KEY) || "";
export const setInstitute  = slug => {
  if (slug) localStorage.setItem(INSTITUTE_KEY, slug);
  else localStorage.removeItem(INSTITUTE_KEY);
  // Switching institutes always means a fresh login for that institute.
  setToken(null); setStoredUser(null);
};

export const getPlatformToken = () => localStorage.getItem(PLATFORM_TOKEN_KEY) || "";
export const setPlatformToken = t => t ? localStorage.setItem(PLATFORM_TOKEN_KEY, t) : localStorage.removeItem(PLATFORM_TOKEN_KEY);

export class ApiError extends Error {
  constructor(message, status, payload) { super(message); this.status = status; this.payload = payload; }
}

// Public deep-links (certificate verify, event pages, feedback, approval
// emails) carry ?inst=<slug> so they work even if the visitor never went
// through the institute picker. This takes priority over the stored pick.
function resolveInstitute() {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("inst");
    if (fromUrl) { localStorage.setItem(INSTITUTE_KEY, fromUrl); return fromUrl; }
  } catch {}
  return getInstitute();
}

async function get(params = {}) {
  const url = new URL(API_URL);
  Object.entries(params).forEach(([k, v]) => { if (v != null && v !== "") url.searchParams.set(k, String(v)); });
  const token = getToken();
  if (token) url.searchParams.set("token", token);
  const inst = resolveInstitute();
  if (inst) url.searchParams.set("inst", inst); // also accepted as a header below
  const res = await fetch(url.toString(), { headers: inst ? { "X-Institute": inst } : {} });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(data?.message || "Request failed", res.status, data);
  return data;
}

// Same shape as get() above, minus the automatic getToken() attachment —
// see the long comment on the kioskGet* client methods for why that
// matters here specifically.
async function kioskGet(params = {}) {
  const url = new URL(API_URL);
  Object.entries(params).forEach(([k, v]) => { if (v != null && v !== "") url.searchParams.set(k, String(v)); });
  const inst = resolveInstitute();
  if (inst) url.searchParams.set("inst", inst);
  const res = await fetch(url.toString(), { headers: inst ? { "X-Institute": inst } : {} });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(data?.message || "Request failed", res.status, data);
  return data;
}

async function post(body = {}) {
  const token = getToken();
  const inst = resolveInstitute();
  const res = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(inst ? { "X-Institute": inst } : {}) },
    body: JSON.stringify({ ...body, token: token || undefined }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(data?.message || "Request failed", res.status, data);
  if (!data.success && data.authError) {
    setToken(null); setStoredUser(null);
    throw new ApiError("Session expired. Please sign in again.", 401, data);
  }
  if (!data.success && data.message) throw new ApiError(data.message, res.status, data);
  return data;
}

// ── Institute picker (no X-Institute header needed for these) ─────────
export async function listInstitutes() {
  const res = await fetch(API_URL + "/api/institutes");
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new ApiError(data?.message || "Could not load institutes", res.status, data);
  return data.institutes || [];
}

// Resolve which institute(s) an email's domain belongs to — lets sign-in
// skip the manual picker for the common case (one domain -> one institute).
export async function resolveInstituteByEmail(email) {
  const res = await fetch(API_URL + "/api/institutes/resolve?email=" + encodeURIComponent(email));
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new ApiError(data?.message || "Could not resolve institute", res.status, data);
  return data.institutes || [];
}

// ── Platform admin (institute registry itself — separate auth) ────────
async function platformFetch(path, opts = {}) {
  const token = getPlatformToken();
  const res = await fetch(API_URL + "/api/platform/" + path, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: "Bearer " + token } : {}), ...(opts.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) throw new ApiError(data?.message || "Request failed", res.status, data);
  return data;
}
export const platformApi = {
  login:            (key)          => platformFetch("login", { method: "POST", body: JSON.stringify({ key }) }),
  listInstitutes:   ()              => platformFetch("institutes"),
  saveInstitute:    (inst)          => platformFetch("institutes", { method: "POST", body: JSON.stringify(inst) }),
  deleteInstitute:  (slug)          => platformFetch("institutes?slug=" + encodeURIComponent(slug), { method: "DELETE" }),
};

const api = {
  // ── AI assistant ─────────────────────────────────────────────────
  aiAssist:            (task, input)            => post({ action: "aiAssist", task, input }),
  aiAsk:               (question)               => post({ action: "aiAsk", question }),
  aiSummarizeFeedback: (bookingID)              => post({ action: "aiSummarizeFeedback", bookingID }),
  aiReviewBooking:     (bookingID)              => post({ action: "aiReviewBooking", bookingID }),
  aiInsights:          ()                       => post({ action: "aiInsights" }),
  listAllFaqs:         ()                       => get({ action: "listAllFaqs" }),
  saveFaq:             (faq)                    => post({ action: "saveFaq", faq }),
  deleteFaq:           (id)                     => post({ action: "deleteFaq", id }),

  // ── HOD approval workflow ───────────────────────────────────────
  listDepartments:     ()                       => get({ action: "listDepartments" }),
  listAllDepartments:  ()                       => get({ action: "listAllDepartments" }),
  saveDepartment:      (dept)                   => post({ action: "saveDepartment", dept }),
  deleteDepartment:    (id)                      => post({ action: "deleteDepartment", id }),
  adminListPendingEvents: ()                     => get({ action: "adminListPendingEvents" }),
  adminReviewEvent:    (kind, eid, decision, note) => post({ action: "adminReviewEvent", kind, eid, decision, note }),
  getApprovalRequest:  (atk)                     => get({ action: "getApprovalRequest", atk }),
  submitApproval:      (atk, decision, note)     => post({ action: "submitApproval", atk, decision, note }),

  // ── Auth ─────────────────────────────────────────────────────────
  requestOtp:        (email)              => post({ action: "requestOtp", email }),
  verifyOtp:         (email, code)        => post({ action: "verifyOtp", email, code }),
  whoami:            ()                   => get({ action: "whoami" }),
  photographerLogin: (email, code, subAction) => post({ action: "photographerLogin", subAction, email, code }),

  // ── Halls ─────────────────────────────────────────────────────────
  listHalls:         ()                   => get({ action: "listHalls" }),
  checkAvail:        (hall, date, ts)     => get({ action: "checkAvailability", hall, date, timeSlot: ts }),
  checkAvailRange:   (hall, s, e, ts)     => get({ action: "checkAvailabilityRange", hall, startDate: s, endDate: e, timeSlot: ts }),

  // ── Bookings ──────────────────────────────────────────────────────
  myBookings:        ()                   => get({ action: "listMyBookings" }),
  trackBooking:      (bookingID)          => get({ action: "trackBookingStatus", bookingID }),
  addBooking:        (b)                  => post({ action: "addBooking", ...b }),
  cancelBooking:     (bookingID)          => post({ action: "cancelBooking", bookingID }),
  editBooking:       (bookingID, updates) => post({ action: "editBooking", bookingID, updates }),
  uploadLetter:      (bookingID, fileName, mimeType, data) =>
                                            post({ action: "uploadPermissionLetter", bookingID, fileName, mimeType, data }),
  uploadSpeakerPhoto:(refID, fileName, mimeType, data) =>
                                            post({ action: "uploadSpeakerPhoto", refID, fileName, mimeType, data }),
  uploadBanner:      (refID, fileName, mimeType, data) =>
                                            post({ action: "uploadBanner", refID, fileName, mimeType, data }),

  // ── Admin bookings ────────────────────────────────────────────────
  adminListBookings: async (opts = {}) => {
    const r = await get({ action: "adminListBookings", ...opts });
    const rows = Array.isArray(r?.bookings) ? r.bookings : [];
    // Worker returns raw snake_case DB rows; map to the camelCase the admin UI
    // expects. Without this bookingID is undefined and status updates fail with
    // "Booking not found".
    const bookings = rows.map((b) => ({
      ...b,
      bookingID:        b.bookingID        ?? b.booking_id,
      eventName:        b.eventName        ?? b.event_name,
      auditorium:       b.auditorium       ?? b.hall_name,
      hallId:           b.hallId           ?? b.hall_id,
      deptClub:         b.deptClub         ?? b.dept_club,
      timeSlot:         b.timeSlot         ?? b.time_slot,
      contactInfo:      b.contactInfo      ?? b.contact_info,
      specialReqs:      b.specialReqs      ?? b.special_reqs,
      permissionLetter: b.permissionLetter ?? b.permission_letter,
      adminNote:        b.adminNote        ?? b.admin_note,
      seriesTotal:      b.seriesTotal      ?? b.series_total,
      createdAt:        b.createdAt        ?? b.created_at,
    }));
    return { ...r, bookings };
  },
  adminUpdateStatus: (bookingID, newStatus, note) =>
                                            post({ action: "adminUpdateStatus", bookingID, newStatus, note }),
  adminEditBooking:  (bookingID, updates) => post({ action: "adminEditBooking", bookingID, updates }),
  adminGetBookingLog:(bookingID)          => get({ action: "adminGetBookingLog", bookingID }),

  // ── Calendar ──────────────────────────────────────────────────────
  calendarData:      (startDate, endDate) => get({ action: "getCalendarData", startDate, endDate }),
  eventsByDate:      (date)               => get({ action: "getEventsByDate", date }),

  // ── Events ────────────────────────────────────────────────────────
  publicFeed:        (opts = {})          => get({ action: "publicEventsFeed", ...opts }),
  recentFeed:        ()                   => get({ action: "recentEventsFeed" }),
  getEventProfile:   (id, qrToken)        => get({ action: "getEventProfile", bookingID: id, qrToken: qrToken || "" }),
  saveEventProfile:  (bookingID, fields)  => post({ action: "saveEventProfile", bookingID, fields }),
  getStandaloneEvent:(eventID)            => get({ action: "getStandaloneEvent", eventID }),
  saveStandaloneEvent:(eventID, fields)   => post({ action: "saveStandaloneEvent", eventID, fields }),
  myStandaloneEvents:()                   => get({ action: "listMyStandaloneEvents" }),
  deleteStandaloneEvent:(eventID)         => post({ action: "deleteStandaloneEvent", eventID }),
  myActivity:        ()                   => get({ action: "getMyActivity" }),

  // ── Registrations ─────────────────────────────────────────────────
  register:          (bookingID, formData) => post({ action: "registerParticipant", bookingID, formData }),
  cancelReg:         (regID, email)        => post({ action: "cancelRegistration", regID, email }),
  listRegs:          (bookingID)           => get({ action: "listRegistrations", bookingID }),
  // exportRegistrations is POST-only in the worker (reads s('bookingID') from body)
  exportRegs:        (bookingID)           => post({ action: "exportRegistrations", bookingID }),
  getMyRegistration: (regID, email)        => get({ action: "getMyRegistration", regID, email }),
  // adminApproveReg: worker reads s('action') for approve/reject AND routes on body.action
  // Send as regAction field; worker updated to read s('regAction')||s('action')
  adminApproveReg:   (regID, regAction)    => post({ action: "adminApproveReg", regID, regAction }),
  adminRemoveReg:    (regID)               => post({ action: "adminRemoveReg", regID }),
  bulkApprove:       (bookingID, regIDs, bulkAction) =>
                                            post({ action: "bulkApproveRegs", bookingID, regIDs, regAction: bulkAction }),
  broadcast:         (bookingID, subject, message) =>
                                            post({ action: "broadcastToRegistrants", bookingID, subject, message }),

  // ── Event team access (owner + coordinators) ──────────────────────
  // The event's creator is always the owner. The owner can grant other
  // staff ("coordinators") delegated access to registrations, check-in
  // and feedback for that specific event. Enforcement lives in the Worker.
  listEventTeam:     (bookingID)           => get({ action: "listEventTeam", bookingID }),
  addCoordinator:    (bookingID, email, perms) =>
                                            post({ action: "addEventCoordinator", bookingID, email, perms }),
  removeCoordinator: (bookingID, email)    => post({ action: "removeEventCoordinator", bookingID, email }),
  updateCoordinator: (bookingID, email, perms) =>
                                            post({ action: "updateEventCoordinator", bookingID, email, perms }),

  // ── Check-in ──────────────────────────────────────────────────────
  checkin:           (bookingID, query, gate, dayIndex) =>
                                            post({ action: "checkinManual", bookingID, query, gate, dayIndex }),
  addWalkIn:         (bookingID, name, email, usn, gate, dayIndex) =>
                                            post({ action: "addWalkIn", bookingID, name, email, usn, gate, dayIndex }),
  attendance:        (bookingID)           => get({ action: "getAttendanceSummary", bookingID }),

  // ── Post-event ────────────────────────────────────────────────────
  getPostEvent:      (bookingID)           => get({ action: "getPostEvent", bookingID }),
  savePostEvent:     (bookingID, fields)   => post({ action: "savePostEvent", bookingID, fields }),
  // getEventReport is POST-only in the worker (reads s('bookingID') from body)
  getReport:         (bookingID)           => post({ action: "getEventReport", bookingID }),
  issueCerts:        (bookingID, minDays)  => post({ action: "issueCertificates", bookingID, minDays }),
  revokeCert:        (certID)              => post({ action: "revokeCertificate", certID }),
  verifyCert:        (token)               => get({ action: "verifyCertificate", token }),
  closeEvent:        (bookingID)           => post({ action: "closeEvent", bookingID }),

  // ── Feedback ──────────────────────────────────────────────────────
  listFeedback:      (bookingID)           => get({ action: "listFeedback", bookingID }),
  submitFeedback:    (regID, email, ratings, comments, isAnon) =>
                                            post({ action: "submitFeedback", regID, email, ratings, comments, isAnon }),

  // ── Gallery ───────────────────────────────────────────────────────
  myGalleryEvents:   ()                    => get({ action: "listMyGalleryEvents" }),
  pendingGalleries:  ()                    => get({ action: "listPendingGalleries" }),
  galleryAll:        (bookingID)           => get({ action: "getEventGalleryAll", bookingID }),
  gallery:           (bookingID)           => get({ action: "getEventGallery", bookingID }),
  uploadGalleryPhoto:(body)                => post({ action: "uploadGalleryPhoto", ...body }),
  submitGalleryPhotos:(bookingID, photos)  => post({ action: "submitGalleryPhotos", bookingID, photos }),
  reviewPhoto:       (photoID, reviewAction, reason) =>
                                            post({ action: "reviewGalleryPhoto", photoID, reviewAction, reason }),
  publishGallery:    (bookingID)           => post({ action: "publishGallery", bookingID }),

  // ── Super Admin ───────────────────────────────────────────────────
  saListHalls:       ()                    => get({ action: "saListHalls" }),
  saSaveHall:        (hall, isNew)         => post({ action: "saSaveHall", hall, isNew }),
  saSetHallStatus:   (hallId, status, maintenanceNote) =>
                                            post({ action: "saSetHallStatus", hallId, status, maintenanceNote }),
  listPhotographers: ()                    => get({ action: "listPhotographers" }),
  addPhotographer:   (email, name)         => post({ action: "addPhotographer", email, name }),
  removePhotographer:(email)               => post({ action: "removePhotographer", email }),

  // ── Institute branding / allowed users (multi-tenant) ──────────────
  instituteMeta:       ()                  => get({ action: "instituteMeta" }),
  saListAllowedUsers:  (q)                 => get({ action: "saListAllowedUsers", q }),
  saBulkUploadUsers:   (users)             => post({ action: "saBulkUploadUsers", users }),
  saRemoveAllowedUser: (email)             => post({ action: "saRemoveAllowedUser", email }),
  selfRegisterAllowedUser: (email, name)   => post({ action: "selfRegisterAllowedUser", email, name }),
};

/* ---- SSO with Solution (helpdesk) ----
   Mints a 60-second single-use hand-off and sends the browser to Solution with the
   token in the URL FRAGMENT, which is never transmitted to a server. */
export async function openSolution() {
  const r = await post({ action: "ssoIssue", token: getToken() });
  if (!r || !r.success || !r.token) throw new Error((r && r.message) || "Could not open the helpdesk.");
  window.location.href = r.redirect + "#t=" + encodeURIComponent(r.token);
}

// ── Graduation Day module (isolated namespace — none of these touch
//    the main `api` object above or any of its existing methods) ──────
export const gradApi = {
  // event linking & access (v8.0)
  setupEvent:        (fields)                => post({ action: "gradSetupEvent", fields }),
  getEventMeta:      ()                      => get({ action: "gradGetEventMeta" }),
  getMyAccess:       ()                      => get({ action: "gradGetMyAccess" }),
  // student
  requestOtp:       (email)                => post({ action: "gradRequestOtp", email }),
  verifyOtp:        (email, code)          => post({ action: "gradVerifyOtp", email, code }),
  // otpToken (optional, added on top of the existing otpVerifiedEmail arg —
  // nothing removed, nothing renamed): the signed proof-of-verification
  // gradVerifyOtp now returns, so the server can actually check "this was
  // really verified" instead of trusting the plain email string on its own.
  lookupSelf:       (usn, otpVerifiedEmail, otpToken) => post({ action: "gradLookupStudentSelf", usn, otpVerifiedEmail, otpToken }),
  resendQrPass:     (usn, email)           => post({ action: "gradResendQrPass", usn, email }),
  confirmStudentByUsn: (usn, mobile, gender, email, officialEmail, familyGuardianCount, acknowledged, customAns, otpVerifiedEmail, otpToken) =>
                                            post({ action: "gradConfirmStudentByUsn", usn, mobile, gender, email, officialEmail, familyGuardianCount, acknowledged, customAns, otpVerifiedEmail, otpToken }),
  updateFamilyCount: (usn, familyGuardianCount) =>
                                            post({ action: "gradUpdateFamilyCount", usn, familyGuardianCount }),
  confirmStudent:   (email, usn, guardianCountPlanned) =>
                                            post({ action: "gradConfirmStudent", email, usn, guardianCountPlanned }),
  // guardian (public, no login — gated by USN lookup instead)
  lookupForGuardian: (usn)                 => post({ action: "gradLookupForGuardian", usn }),
  registerGuardian:  (usn, fields)         => post({ action: "gradRegisterGuardian", usn, fields }),
  cancelStudentConfirmation: (usn, reason)             => post({ action: "gradCancelStudentConfirmation", usn, reason }),
  cancelGuardianRegistration: (guardianId, reason)     => post({ action: "gradCancelGuardianRegistration", guardianId, reason }),
  getGuardiansForStudent: (usn)                        => post({ action: "gradGetGuardiansForStudent", usn }),
  // department → coordinator assignment (v8.7) — no physical "gate"
  // concept; coordinators are identified by who's logged in, not a label.
  getDeptAssignments:    ()                        => get({ action: "gradGetDeptAssignments" }),
  saveDeptAssignment:    (dept, coordinatorEmail, entryStart, entryEnd) =>
                                                    post({ action: "gradSaveDeptAssignment", dept, coordinatorEmail, entryStart, entryEnd }),
  deleteDeptAssignment:  (dept, coordinatorEmail)  => post({ action: "gradDeleteDeptAssignment", dept, coordinatorEmail }),
  getInvitationConfig:   ()                        => get({ action: "gradGetInvitationConfig" }),
  // Award-ceremony kiosk display — a separate password gate, not the
  // regular coordinator/host login. setKioskPassword/getKioskPasswordStatus/
  // kioskLogin are host-side actions (called from the logged-in dashboard,
  // so the regular user token is exactly what they should carry) — but the
  // three kioskGet* calls below run FROM the kiosk device itself and must
  // NOT go through get()'s automatic token attachment: get() unconditionally
  // overwrites any token passed in params with whatever regular user
  // happens to be logged into that browser (getToken()), which would
  // silently replace the kiosk's own token on a shared/reused device.
  // These build the request directly, carrying only the explicit kiosk
  // token, with no dependency on getToken() at all.
  setKioskPassword:       (password)                => post({ action: "gradSetKioskPassword", password }),
  getKioskPasswordStatus: ()                        => get({ action: "gradGetKioskPasswordStatus" }),
  getCeremonyStatus:      ()                        => get({ action: "gradGetCeremonyStatus" }),
  getCeremonyPdfData:     (presence = "present")    => get({ action: "gradGetCeremonyPdfData", presence }),
  setCeremonyRegistrationClosed: (closed)           => post({ action: "gradSetCeremonyRegistrationClosed", closed }),
  kioskLogin:             (password)                => post({ action: "gradKioskLogin", password }),
  kioskGetDepartments:    (kioskToken)               => kioskGet({ action: "gradKioskGetDepartments", token: kioskToken }),
  kioskGetDeptAttendance: (kioskToken, dept)         => kioskGet({ action: "gradKioskGetDeptAttendance", token: kioskToken, dept }),
  kioskGetRankHolders:    (kioskToken)               => kioskGet({ action: "gradKioskGetRankHolders", token: kioskToken }),
  kioskGetHonorsStudents: (kioskToken)               => kioskGet({ action: "gradKioskGetHonorsStudents", token: kioskToken }),
  kioskGetCeremonyAttendance: (kioskToken)            => kioskGet({ action: "gradKioskGetCeremonyAttendance", token: kioskToken }),
  saveInvitationConfig:  (config)                  => post({ action: "gradSaveInvitationConfig", config }),
  uploadInvitationPdf:   (fileName, mimeType, data) => post({ action: "gradUploadInvitationPdf", fileName, mimeType, data }),
  uploadImage:           (fileName, mimeType, data) => post({ action: "gradUploadImage", fileName, mimeType, data }),
  getCanonicalDepts:     ()                        => get({ action: "gradGetCanonicalDepts" }),
  addCanonicalDept:      (deptCode, label, level)   => post({ action: "gradAddCanonicalDept", deptCode, label, level }),
  // coordinator-scoped attendance status for their assigned department(s)
  getMyDeptStatus:       ()                        => get({ action: "gradGetMyDeptStatus" }),
  // staff — scanning & walk-ins
  scanQr:            (qrToken)             => post({ action: "gradScanQr", qrToken }),
  walkinLookupStudent: (usn)               => post({ action: "gradWalkinLookupStudent", usn }),
  walkinSearchStudents: (search)           => get({ action: "gradWalkinSearchStudents", search }),
  walkinConfirm:       (usn)               => post({ action: "gradWalkinConfirm", usn }),
  walkinGuardian:       (usn, fields)      => post({ action: "gradWalkinGuardian", usn, fields }),
  // staff — dashboard
  getOverview:        ()                   => get({ action: "gradGetOverview" }),
  getOverviewByDept:  ()                   => get({ action: "gradGetOverviewByDept" }),
  // mode: pass "honors" for the honors-degree sheet; rankOnly still works
  // exactly as before for the rank sheet (kept, not replaced, so nothing
  // else calling this with just rankOnly needs to change).
  getAttendanceSheet: (dept, rankOnly, presence, mode) => get({ action: "gradGetAttendanceSheet", dept, rankOnly: rankOnly ? "1" : "", presence: presence || "", mode: mode || "" }),
  exportRosterByCategory: () => post({ action: "gradExportRosterByCategory" }),
  getUnconfirmedStudents: (dept) => get({ action: "gradGetUnconfirmedStudents", dept: dept || "" }),
  // admin
  importRoster:      (students)            => post({ action: "gradImportRoster", students }),
  setStudentPersonalEmail: (usn, personalEmail) => post({ action: "gradSetStudentPersonalEmail", usn, personalEmail }),
  bulkMarkRankHolders: (usns, rankLabel)   => post({ action: "gradBulkMarkRankHolders", usns, rankLabel }),
  importRankHolders:  (rows)               => post({ action: "gradImportRankHolders", rows }),
  importHonorsStudents: (rows)             => post({ action: "gradImportHonorsStudents", rows }),
  listStudents:      (params)              => get({ action: "gradListStudents", ...params }),
  getRosterFilters:  ()                    => get({ action: "gradGetRosterFilters" }),
  deleteStudents:    (params)              => post({ action: "gradDeleteStudents", ...params }),
  getFormFields:     (appliesTo)           => get({ action: "gradGetFormFields", appliesTo }),
  saveFormField:     (field)               => post({ action: "gradSaveFormField", field }),
  deleteFormField:   (fieldId)             => post({ action: "gradDeleteFormField", fieldId }),
};

// Low-level transport shared with feature modules (BICEP) so they inherit the
// same base URL, institute resolution and session handling as the core client.
export { get as apiGet, post as apiPost };

export default api;
